import {
  Box,
  Checkbox,
  Flex,
  Heading,
  Input,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import React, { useState } from 'react';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import {
  FundingRequest,
  typeguards as finleyTypeguards,
} from '@finley-technologies/finley-types';
import { validate as reportingValidate } from '@finley-technologies/reporting-utilities';

import { ReportingOutputDocxFileInsertionOutputContainer } from './docxOutput/ReportingOutputDocxFileInsertionOutputContainer';
import { ReportTemplateContainer } from './template/ReportTemplateContainer';
import {
  AddIconButton,
  DeleteIconButton,
  EditIconButton,
  SaveIconButton,
  XIconButton,
} from 'packages/common-frontend/components/IconButton';
import { InfoAlertWithDescription } from 'packages/common-frontend/components/InfoAlertWithDescription';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { CreditAgreementFullArtifactWipConfigType } from 'packages/creditAgreements/ui/types';

const getDefaultFundingRequestComponentConfig = (): CreditAgreement.Config.Reporting.Outputs.Docx.FundingRequestComponentConfig => ({
  collectionsPaydown: false,
  collectionsRecycle: false,
  curePaydown: false,
  drawRequest: false,
});

export const finleyStandardDocxReportConfigTypes: CreditAgreement.DomainEntity.ReportConfigType[] = [
  'fundingNotice',
  'invoice',
  'disbursementNotice',
  'paymentInKindNotice',
];

type ReportingOutputDocxFundingRequestComponentsConfigProps = {
  fundingRequestComponents?: CreditAgreement.Config.Reporting.Outputs.Docx.FundingRequestComponentConfig;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  basePropertyKey: string;
};
export const ReportingOutputDocxFundingRequestComponentsConfig = ({
  fundingRequestComponents,
  wipConfig,
  basePropertyKey,
}: ReportingOutputDocxFundingRequestComponentsConfigProps) => {
  const [config, setFundingRequestComponentsConfig] = useState<
    | CreditAgreement.Config.Reporting.Outputs.Docx.FundingRequestComponentConfig
    | undefined
  >(fundingRequestComponents);

  return (
    <Box
      id="funding-request-component-config-container"
      mb="4"
      borderWidth="1px"
      p="4"
    >
      <Flex mb="5" alignItems="center">
        <Heading fontSize="large" as="h4" mr="3">
          Funding Request Components
        </Heading>

        {wipConfig?.isMutateAllowed(basePropertyKey) && (
          <>
            {config ? (
              <DeleteIconButton
                ariaLabel="remove optional config"
                label="Remove Optional Config"
                onClick={() => {
                  wipConfig.formProps.setFieldValue(basePropertyKey, undefined);
                  setFundingRequestComponentsConfig(undefined);
                }}
              />
            ) : (
              <AddIconButton
                onClick={() => {
                  const defaultConfig = getDefaultFundingRequestComponentConfig();
                  wipConfig.formProps.setFieldValue(
                    basePropertyKey,
                    getDefaultFundingRequestComponentConfig(),
                  );
                  setFundingRequestComponentsConfig(defaultConfig);
                }}
                label="Add Optional Config"
                ariaLabel="add optional config"
              />
            )}
          </>
        )}
      </Flex>

      {config ? (
        <Table style={{ tableLayout: 'fixed' }} borderWidth="1px">
          <Thead>
            <Tr>
              <Th>Component Type</Th>
              <Th>isActive</Th>
            </Tr>
          </Thead>
          <Tbody>
            {Object.values(FundingRequest.FundingRequestComponentType).map(
              (type) => {
                return (
                  <Tr key={type}>
                    <Td>{commonUtils.toTitleCase(type)}</Td>
                    <Td>
                      <Checkbox
                        ml="2"
                        isChecked={config[type]}
                        isDisabled={
                          !wipConfig?.isMutateAllowed(basePropertyKey)
                        }
                        onChange={(e) => {
                          wipConfig?.formProps.setFieldValue(
                            `${basePropertyKey}.${type}`,
                            e.target.checked,
                          );
                          const newConfig: CreditAgreement.Config.Reporting.Outputs.Docx.FundingRequestComponentConfig = {
                            ...config,
                          };
                          newConfig[type] = e.target.checked;
                          setFundingRequestComponentsConfig(newConfig);
                        }}
                      />
                    </Td>
                  </Tr>
                );
              },
            )}
          </Tbody>
        </Table>
      ) : (
        <Text>Funding request components not currently configured.</Text>
      )}
    </Box>
  );
};

type ReportingOutputDocxConfigProps = {
  organizationId: string;
  creditAgreementId: string;
  outputConfig: CreditAgreement.Config.Reporting.Outputs.Docx.Config;
  reportConfigTypes: CreditAgreement.DomainEntity.ReportConfigType[];
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  basePropertyKey: string;
};
export const ReportingOutputDocxConfig = ({
  organizationId,
  creditAgreementId,
  outputConfig,
  reportConfigTypes,
  wipConfig,
  basePropertyKey,
}: ReportingOutputDocxConfigProps) => {
  const { name, template, fundingRequestComponents, data } = outputConfig;
  const toast = useToast();

  // *** Output Name ***
  const [outputName, setOutputName] = useState<string>(name);
  const [isOutputNameEditOpen, setisOutputNameEditOpen] = useState<boolean>(
    false,
  );

  const updateOutputName = (): void => {
    if (!wipConfig?.isMutateAllowed(basePropertyKey)) {
      return;
    }

    const wipOutputConfig = getIn(wipConfig.formProps.values, basePropertyKey);
    finleyAssert(
      finleyTypeguards.creditAgreement.isReportingOutputsDocxConfig(
        wipOutputConfig,
      ),
      `Retrieved non-Output Config structure for [key:${basePropertyKey}]`,
    );

    wipConfig.formProps.setFieldValue(`${basePropertyKey}.name`, outputName);
  };

  const reportTypeConfigsWithFinleyStandardTemplate: CreditAgreement.DomainEntity.ReportConfigType[] = reportConfigTypes.filter(
    (reportConfigType) =>
      finleyStandardDocxReportConfigTypes.includes(reportConfigType),
  );

  return (
    <Box id="reporting-output-config-container">
      <Flex alignItems="center" mb="3">
        <Text as="h2" fontWeight="bold" fontSize="xl" mr="2">
          Output Config
        </Text>
        {wipConfig?.isMutateAllowed(basePropertyKey) && (
          <>
            <SaveIconButton
              ariaLabel="save output config"
              label="Save Output Config"
              onClick={() => {
                const newOutputConfig = getIn(
                  wipConfig.formProps.values,
                  basePropertyKey,
                );
                finleyAssert(
                  finleyTypeguards.creditAgreement.isReportingOutputsDocxConfig(
                    newOutputConfig,
                  ),
                  `Retrieved non-Output DOCX Config structure for [key:${basePropertyKey}]`,
                );

                const outputErrors = reportingValidate.output.validateReportingOutputDocxConfig(
                  { config: newOutputConfig },
                );

                if (outputErrors.length > 0) {
                  toast({
                    title: 'Saving Output Config Error',
                    description: outputErrors.join(', '),
                    status: 'error',
                    position: 'top',
                    duration: 8000,
                    isClosable: true,
                  });
                  return;
                }

                wipConfig.saveAndUpdateConfigCreditAgreementWip({
                  wipConfig,
                  propertyKey: basePropertyKey,
                  updatedProperty: newOutputConfig,
                });
              }}
            />
          </>
        )}
      </Flex>

      {wipConfig?.isMutateAllowed(basePropertyKey) && (
        <Text fontStyle="italic" fontSize="md" mb="6">
          Note that this &quot;save&quot; only saves the Output config portion
        </Text>
      )}

      <Flex
        alignItems="center"
        border="1px"
        borderColor="gray.700"
        p="3"
        mb="4"
      >
        <Text width="56" mr="3">
          Generated Report Name:
        </Text>
        {isOutputNameEditOpen && wipConfig?.isMutateAllowed(basePropertyKey) ? (
          <>
            <Input
              mr="2"
              type="text"
              width="96"
              value={outputName}
              onChange={(e) => setOutputName(e.target.value)}
            />
            <SaveIconButton
              ariaLabel="save name"
              label="Save Name"
              onClick={() => {
                updateOutputName();
                setisOutputNameEditOpen(false);
              }}
              isDisabled={!outputName}
            />
            <XIconButton
              ariaLabel="close edit"
              label="Close Edit"
              onClick={() => setisOutputNameEditOpen(false)}
            />
          </>
        ) : (
          <>
            <Text width="96" px="4">
              {outputName}
            </Text>
            {wipConfig?.isMutateAllowed(basePropertyKey) && (
              <EditIconButton
                ariaLabel="edit name"
                label="Edit Name"
                onClick={() => setisOutputNameEditOpen(true)}
                ml="2"
              />
            )}
          </>
        )}
      </Flex>

      {!!reportTypeConfigsWithFinleyStandardTemplate.length && (
        <Box mb="16px">
          {/* @dani update the alert when it's fully live */}
          <InfoAlertWithDescription
            description={`For Servicing Platform product report [${reportTypeConfigsWithFinleyStandardTemplate.join(
              ', ',
            )}], Finley standard template will be used.`}
          />
        </Box>
      )}

      {!reportTypeConfigsWithFinleyStandardTemplate.length && (
        <>
          <ReportingOutputDocxFundingRequestComponentsConfig
            fundingRequestComponents={fundingRequestComponents}
            wipConfig={wipConfig}
            basePropertyKey={`${basePropertyKey}.fundingRequestComponents`}
          />

          <ReportingOutputDocxFileInsertionOutputContainer
            organizationId={organizationId}
            basePropertyKey={`${basePropertyKey}.data`}
            fileInsertionOutputs={data}
            wipConfig={wipConfig}
          />

          <ReportTemplateContainer
            creditAgreementId={creditAgreementId}
            reportConfigType={reportConfigTypes[0]}
            reportName={name}
            templateConfig={template}
            templatePropertyKey={`${basePropertyKey}.template`}
            wipConfig={wipConfig}
            fileType="docx"
          />
        </>
      )}
    </Box>
  );
};
